'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document } from '@contentful/rich-text-types';
import { ArrowUpRight } from 'lucide-react';

import { BrandHeroButtons } from '@/modules/brand/components/BrandHero/BrandHeroButtons';
import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Button } from '@/modules/common/components/Button';
import Image from '@/modules/common/components/Image';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@/modules/common/components/carousel';
import { Chevron } from '@/modules/common/components/icons';
import { PromotionSign } from '@/modules/common/components/icons/Promotion';
import Link from '@/modules/common/components/ui/Link';
import PromotionModal from '@/modules/product/components/modal/PromotionModal';

import { BrandResultMohawk, ContentfullAsset } from '@/gql/__generated/graphql.types';

type BrandHeroProps = {
  description?: Document;
  officialSiteUrl?: string | null;
  brandImages?: ContentfullAsset[];
  brandLogo?: ContentfullAsset;
  brandData?: BrandResultMohawk | null;
  brandName?: string | null;
};

export const BrandHero = ({
  description,
  officialSiteUrl = '#',
  brandImages = [],
  brandLogo,
  brandData,
  brandName,
}: BrandHeroProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      setShowButton(contentRef.current.scrollHeight > 100);
    }
  }, [description]);

  const subscribeToCarouselSelection = useCallback(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  useEffect(() => {
    subscribeToCarouselSelection();
  }, [subscribeToCarouselSelection]);

  return (
    <div className="mb-10 flex flex-col lg:max-h-[511px] lg:flex-row" data-testid="brand-hero">
      <div
        className="order-2 border-[#F2F2F2] p-8 lg:w-1/2 lg:border-b"
        data-testid="brand-hero-content"
      >
        <h1>{brandName}</h1>
        <div className="mb-6 flex items-center justify-between lg:flex-col lg:items-start">
          <Image
            src={brandLogo?.url || ''}
            alt={brandLogo?.fileName || 'Brand logo'}
            className="max-h-12 lg:mb-6 lg:max-h-24"
            data-testid="brand-logo"
          />
          <Link href={officialSiteUrl || '#'} rel={undefined} data-testid="official-site-link">
            Official Site <ArrowUpRight className="ml-1 h-4 w-4" />
          </Link>
        </div>

        <div className="">
          <div
            className={`text-base ${isExpanded ? 'max-h-none' : 'max-h-[100px] overflow-hidden'} transition-all`}
            ref={contentRef}
            data-testid="brand-description"
          >
            {description ?
              documentToReactComponents(description, { preserveWhitespace: true })
            : ''}
          </div>
          {showButton && (
            <Button
              variant="link"
              className="mb-4 mt-2 h-auto p-0 font-bold"
              onClick={() => setIsExpanded(!isExpanded)}
              data-testid="toggle-description-button"
            >
              {isExpanded ? 'Less' : 'More'}
            </Button>
          )}
        </div>

        <PromotionModal
          where={DataLayerWhere.BrandPage}
          brandData={brandData}
          data-testid="promotion-modal"
        >
          <Button
            variant="outlined-light"
            className="flex h-auto w-full min-w-56 items-center justify-between gap-1 rounded-none border-b border-gray-75 px-0 py-4"
            data-testid="promotions-button"
          >
            <div className="flex gap-4">
              <PromotionSign />
              <span>Promotions</span>
            </div>
            <Chevron position="right" className="h-6 w-6 px-1 text-gray-500" />
          </Button>
        </PromotionModal>

        <BrandHeroButtons brandData={brandData} data-testid="brand-hero-buttons" />
      </div>

      <div className="relative order-1 lg:order-3 lg:w-1/2" data-testid="brand-images-container">
        {brandImages.length > 1 ?
          <Carousel
            opts={{
              loop: true,
            }}
            className="h-full"
            setApi={setApi}
            data-testid="brand-carousel"
          >
            <CarouselContent className="ml-0 h-full" wrapperClassName="h-full">
              {brandImages.map((image, index) => (
                <CarouselItem
                  key={index}
                  className="h-full w-full pl-0"
                  data-testid={`brand-image-${index}`}
                >
                  <Image
                    src={image.url || ''}
                    alt={image.fileName || `Brand image ${index + 1}`}
                    className="h-full w-full object-cover"
                    data-testid={`brand-image-${index}-img`}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselDots api={api} count={brandImages.length} current={current} />
          </Carousel>
        : <Image
            src={brandImages[0]?.url || ''}
            alt={brandImages[0]?.fileName || 'Brand image'}
            className="aspect-[4/3] h-full max-h-[340px] w-full object-cover sm:max-h-[380px] lg:max-h-full"
          />
        }
      </div>
    </div>
  );
};

// CarouselDots component
const CarouselDots = ({
  api,
  count,
  current,
}: {
  api: CarouselApi | undefined;
  count: number;
  current: number;
}) => {
  return (
    <div className="absolute bottom-4 left-1/2 flex w-fit justify-center gap-1 rounded-full bg-black/20 p-1">
      {Array.from({ length: count }).map((_, index) => (
        <button
          key={index}
          className={`h-2 w-2 rounded-full transition-all ${
            index === current ? 'bg-white' : 'bg-white/50'
          }`}
          onClick={() => api?.scrollTo(index)}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
  );
};
